import React, { useState, useEffect } from 'react';
import {
  Plus,
  Mail,
  Phone,
  Building2,
  DollarSign,
  Calendar,
  ExternalLink,
  Eye,
  Edit,
  Trash2,
  X,
  Activity,
  ListTodo,
  Settings,
  User,
  Users,
  Flame,
  TrendingUp,
  ArrowUpDown,
} from 'lucide-react';
import { useBrand } from '../contexts/BrandContext';
import { useAuth } from '../contexts/AuthContext';
import { useTaskCache } from '../contexts/TaskCacheContext';
import {
  getPipelineStages,
  getOpportunities,
  moveOpportunityToStage,
  deleteOpportunity,
  type Opportunity,
  type PipelineStage,
} from '../lib/crmService';
import ActivityTimeline from './ActivityTimeline';
import ActivityForm from './ActivityForm';
import TaskList from './TaskList';
import TaskTemplateManager from './TaskTemplateManager';
import PipelineFilters, { FilterConfig } from './PipelineFilters';
import PipelineAnalytics from './PipelineAnalytics';
import BulkActions from './BulkActions';
import { scoringService } from '../lib/scoringService';

interface PipelineProps {
  onCreateOpportunity: () => void;
  onEditOpportunity: (opportunity: Opportunity) => void;
}

export default function Pipeline({ onCreateOpportunity, onEditOpportunity }: PipelineProps) {
  const { brands, selectedBrand, selectedBrandIds } = useBrand();
  const { profile } = useAuth();
  const { preloadTasks } = useTaskCache();
  const [stages, setStages] = useState<PipelineStage[]>([]);
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [draggedOpportunity, setDraggedOpportunity] = useState<string | null>(null);
  const [selectedOpportunity, setSelectedOpportunity] = useState<Opportunity | null>(null);
  const [showActivityForm, setShowActivityForm] = useState(false);
  const [showTaskTemplateManager, setShowTaskTemplateManager] = useState(false);
  const [activeTab, setActiveTab] = useState<'details' | 'timeline' | 'tasks'>('details');
  const [showMyLeadsOnly, setShowMyLeadsOnly] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [filters, setFilters] = useState<FilterConfig>({
    search: '',
    valueMin: null,
    valueMax: null,
    closeDate: 'all',
    probability: 'all',
    priority: 'all',
    source: [],
    tags: [],
  });
  const [sortBy, setSortBy] = useState<'priority' | 'value' | 'date'>('priority');

  useEffect(() => {
    if (selectedBrandIds.length > 0) {
      loadPipelineData();
    }
  }, [selectedBrandIds, showMyLeadsOnly]);

  useEffect(() => {
    setSelectedIds([]);
  }, [filters, showMyLeadsOnly]);

  const loadPipelineData = async () => {
    if (selectedBrandIds.length === 0) return;

    try {
      setLoading(true);
      const allStagesMap = new Map<string, PipelineStage>();
      const allOpportunities: Opportunity[] = [];

      for (const brandId of selectedBrandIds) {
        const [stagesData, oppsData] = await Promise.all([
          getPipelineStages(brandId),
          getOpportunities(brandId, undefined, undefined, { ownedByMe: showMyLeadsOnly }),
        ]);

        stagesData.forEach(stage => {
          const key = `${stage.name}-${stage.order_index}`;
          if (!allStagesMap.has(key)) {
            allStagesMap.set(key, stage);
          }
        });

        allOpportunities.push(...oppsData);
      }

      const consolidatedStages = Array.from(allStagesMap.values())
        .sort((a, b) => a.order_index - b.order_index);

      setStages(consolidatedStages);
      setOpportunities(allOpportunities);
    } catch (error) {
      console.error('Error loading pipeline data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (opportunityId: string) => {
    setDraggedOpportunity(opportunityId);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = async (stageId: string) => {
    if (!draggedOpportunity) return;

    const success = await moveOpportunityToStage(draggedOpportunity, stageId);
    if (success) {
      await loadPipelineData();
    }
    setDraggedOpportunity(null);
  };

  const handleDelete = async (opportunityId: string) => {
    if (confirm('Are you sure you want to delete this opportunity?')) {
      const success = await deleteOpportunity(opportunityId);
      if (success) {
        await loadPipelineData();
        setSelectedOpportunity(null);
      }
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const applyFilters = (opps: Opportunity[]): Opportunity[] => {
    return opps.filter((opp) => {
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const matchesSearch =
          opp.client_name.toLowerCase().includes(searchLower) ||
          opp.email.toLowerCase().includes(searchLower) ||
          (opp.company_name && opp.company_name.toLowerCase().includes(searchLower));
        if (!matchesSearch) return false;
      }

      if (filters.valueMin !== null && Number(opp.value) < filters.valueMin) return false;
      if (filters.valueMax !== null && Number(opp.value) > filters.valueMax) return false;

      if (filters.probability !== 'all') {
        const prob = opp.probability || 50;
        if (filters.probability === 'high' && prob <= 70) return false;
        if (filters.probability === 'medium' && (prob < 40 || prob > 70)) return false;
        if (filters.probability === 'low' && prob >= 40) return false;
      }

      if (filters.priority && filters.priority !== 'all') {
        if (opp.priority_level !== filters.priority) return false;
      }

      if (filters.closeDate !== 'all' && opp.expected_close_date) {
        const closeDate = new Date(opp.expected_close_date);
        const now = new Date();
        const daysDiff = (closeDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);

        if (filters.closeDate === 'overdue' && daysDiff >= 0) return false;
        if (filters.closeDate === 'this_week' && (daysDiff < 0 || daysDiff > 7)) return false;
        if (filters.closeDate === 'this_month' && (daysDiff < 0 || daysDiff > 30)) return false;
        if (filters.closeDate === 'this_quarter' && (daysDiff < 0 || daysDiff > 90)) return false;
      }

      if (filters.source.length > 0 && !filters.source.includes(opp.source || 'manual')) return false;

      return true;
    });
  };

  const getOpportunitiesByStage = (stageId: string) => {
    const filtered = applyFilters(opportunities);
    const stageOpps = filtered.filter((opp) => opp.pipeline_stage_id === stageId);

    return stageOpps.sort((a, b) => {
      if (sortBy === 'priority') {
        return (b.priority_score || 0) - (a.priority_score || 0);
      } else if (sortBy === 'value') {
        return Number(b.value) - Number(a.value);
      } else {
        const dateA = a.expected_close_date ? new Date(a.expected_close_date).getTime() : 0;
        const dateB = b.expected_close_date ? new Date(b.expected_close_date).getTime() : 0;
        return dateA - dateB;
      }
    });
  };

  const toggleSelectOpportunity = (oppId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedIds((prev) =>
      prev.includes(oppId) ? prev.filter((id) => id !== oppId) : [...prev, oppId]
    );
  };

  const handleBulkActionComplete = () => {
    setSelectedIds([]);
    loadPipelineData();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <h2 className="text-xl md:text-2xl font-bold text-gray-900">Sales Pipeline</h2>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setShowAnalytics(true)}
            className="px-3 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 active:bg-gray-100 flex items-center space-x-2 text-sm shadow-sm"
          >
            <Activity className="w-4 h-4" />
            <span className="hidden sm:inline">Analytics</span>
          </button>
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setSortBy('priority')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${
                sortBy === 'priority'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              title="Sort by priority score"
            >
              <Flame className="w-4 h-4" />
              <span className="hidden lg:inline">Priority</span>
            </button>
            <button
              onClick={() => setSortBy('value')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${
                sortBy === 'value'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              title="Sort by deal value"
            >
              <DollarSign className="w-4 h-4" />
              <span className="hidden lg:inline">Value</span>
            </button>
            <button
              onClick={() => setSortBy('date')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${
                sortBy === 'date'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              title="Sort by expected close date"
            >
              <Calendar className="w-4 h-4" />
              <span className="hidden lg:inline">Date</span>
            </button>
          </div>
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setShowMyLeadsOnly(false)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${
                !showMyLeadsOnly
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Users className="w-4 h-4" />
              <span className="hidden sm:inline">All Team Leads</span>
              <span className="sm:hidden">All</span>
            </button>
            <button
              onClick={() => setShowMyLeadsOnly(true)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${
                showMyLeadsOnly
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <User className="w-4 h-4" />
              <span className="hidden sm:inline">My Leads</span>
              <span className="sm:hidden">Mine</span>
            </button>
          </div>
          <button
            onClick={() => setShowTaskTemplateManager(true)}
            className="px-3 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 active:bg-gray-100 flex items-center space-x-2 text-sm shadow-sm"
          >
            <Settings className="w-4 h-4" />
            <span className="hidden sm:inline">Templates</span>
          </button>
          <button
            onClick={onCreateOpportunity}
            className="px-3 py-2 md:px-4 md:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 flex items-center space-x-2 text-sm md:text-base shadow-sm"
          >
            <Plus className="w-4 h-4 md:w-5 md:h-5" />
            <span className="hidden sm:inline">Create</span>
            <span className="sm:hidden">New</span>
          </button>
        </div>
      </div>

      <PipelineFilters filters={filters} onFiltersChange={setFilters} />

      <div className="hidden md:flex md:space-x-4 md:overflow-x-auto md:pb-4">
        {stages.map((stage) => {
          const stageOpportunities = getOpportunitiesByStage(stage.id);
          const stageValue = stageOpportunities.reduce((sum, opp) => sum + Number(opp.value), 0);

          return (
            <div
              key={stage.id}
              className="flex-shrink-0 w-80 bg-gray-50 rounded-lg shadow-sm"
              onDragOver={handleDragOver}
              onDrop={() => handleDrop(stage.id)}
            >
              <div className="p-4 border-b border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: stage.color }}
                    ></div>
                    <h3 className="font-semibold text-gray-900">{stage.name}</h3>
                  </div>
                  <span className="px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs font-medium">
                    {stageOpportunities.length}
                  </span>
                </div>
                <p className="text-sm text-gray-600 font-medium">
                  {formatCurrency(stageValue)}
                </p>
              </div>

              <div className="p-4 space-y-3 max-h-[calc(100vh-320px)] overflow-y-auto">
                {stageOpportunities.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    <p className="text-sm">No opportunities</p>
                  </div>
                ) : (
                  stageOpportunities.map((opportunity) => (
                    <div
                      key={opportunity.id}
                      draggable
                      onDragStart={() => handleDragStart(opportunity.id)}
                      onClick={() => setSelectedOpportunity(opportunity)}
                      onMouseEnter={() => preloadTasks(opportunity.id)}
                      className={`bg-white p-4 rounded-lg shadow-sm border-2 hover:shadow-md transition-all cursor-pointer active:scale-98 ${
                        selectedIds.includes(opportunity.id) ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                      }`}
                    >
                      <div className="flex items-start gap-2 mb-2">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(opportunity.id)}
                          onChange={(e) => toggleSelectOpportunity(opportunity.id, e)}
                          onClick={(e) => e.stopPropagation()}
                          className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-gray-900 text-sm">
                              {opportunity.client_name}
                            </h4>
                            {opportunity.priority_level && (
                              <span
                                className={`px-1.5 py-0.5 rounded text-xs font-semibold ${
                                  scoringService.getPriorityConfig(opportunity.priority_level).bgColor
                                } ${scoringService.getPriorityConfig(opportunity.priority_level).color} ${
                                  scoringService.getPriorityConfig(opportunity.priority_level).borderColor
                                } border`}
                                title={`Priority Score: ${opportunity.priority_score}/100`}
                              >
                                {scoringService.getPriorityConfig(opportunity.priority_level).icon}
                              </span>
                            )}
                          </div>
                          {opportunity.brand && (
                            <p className="text-xs text-blue-600 font-medium truncate">
                              {opportunity.brand.name}
                            </p>
                          )}
                        </div>
                        <span
                          className="px-2 py-1 rounded text-xs font-medium ml-2 flex-shrink-0"
                          style={{
                            backgroundColor: `${stage.color}20`,
                            color: stage.color,
                          }}
                        >
                          {opportunity.probability}%
                        </span>
                      </div>

                      {opportunity.company_name && (
                        <div className="flex items-center text-xs text-gray-600 mb-2">
                          <Building2 className="w-3 h-3 mr-1" />
                          <span className="truncate">{opportunity.company_name}</span>
                        </div>
                      )}

                      <div className="flex items-center text-xs text-gray-600 mb-2">
                        <Mail className="w-3 h-3 mr-1" />
                        <span className="truncate">{opportunity.email}</span>
                      </div>

                      {opportunity.phone && (
                        <div className="flex items-center text-xs text-gray-600 mb-2">
                          <Phone className="w-3 h-3 mr-1" />
                          <span>{opportunity.phone}</span>
                        </div>
                      )}

                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                        <div className="flex items-center text-sm font-semibold text-gray-900">
                          <DollarSign className="w-4 h-4 mr-1" />
                          {formatCurrency(Number(opportunity.value))}
                        </div>
                        {opportunity.proposal_link && (
                          <ExternalLink className="w-4 h-4 text-blue-600" />
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="md:hidden space-y-4">
        {stages.map((stage) => {
          const stageOpportunities = getOpportunitiesByStage(stage.id);
          const stageValue = stageOpportunities.reduce((sum, opp) => sum + Number(opp.value), 0);

          return (
            <div key={stage.id} className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-4 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <div
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: stage.color }}
                    ></div>
                    <h3 className="font-semibold text-gray-900 text-base">{stage.name}</h3>
                  </div>
                  <span className="px-2 py-1 bg-white text-gray-700 rounded-full text-xs font-medium shadow-sm">
                    {stageOpportunities.length}
                  </span>
                </div>
                <p className="text-sm text-gray-600 font-medium">
                  {formatCurrency(stageValue)}
                </p>
              </div>

              <div className="divide-y divide-gray-100">
                {stageOpportunities.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    <p className="text-sm">No opportunities</p>
                  </div>
                ) : (
                  stageOpportunities.map((opportunity) => (
                    <div
                      key={opportunity.id}
                      onClick={() => setSelectedOpportunity(opportunity)}
                      className="p-4 hover:bg-gray-50 active:bg-gray-100 transition-colors cursor-pointer"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-gray-900 text-sm truncate">
                              {opportunity.client_name}
                            </h4>
                            {opportunity.priority_level && (
                              <span
                                className={`px-1.5 py-0.5 rounded text-xs font-semibold ${
                                  scoringService.getPriorityConfig(opportunity.priority_level).bgColor
                                } ${scoringService.getPriorityConfig(opportunity.priority_level).color} ${
                                  scoringService.getPriorityConfig(opportunity.priority_level).borderColor
                                } border`}
                                title={`Priority Score: ${opportunity.priority_score}/100`}
                              >
                                {scoringService.getPriorityConfig(opportunity.priority_level).icon}
                              </span>
                            )}
                          </div>
                          {opportunity.brand && (
                            <p className="text-xs text-blue-600 font-medium truncate">
                              {opportunity.brand.name}
                            </p>
                          )}
                        </div>
                        <span
                          className="px-2 py-1 rounded text-xs font-medium ml-2 flex-shrink-0"
                          style={{
                            backgroundColor: `${stage.color}20`,
                            color: stage.color,
                          }}
                        >
                          {opportunity.probability}%
                        </span>
                      </div>

                      {opportunity.company_name && (
                        <div className="flex items-center text-xs text-gray-600 mb-2">
                          <Building2 className="w-3 h-3 mr-1 flex-shrink-0" />
                          <span className="truncate">{opportunity.company_name}</span>
                        </div>
                      )}

                      <div className="flex items-center text-xs text-gray-600 mb-2">
                        <Mail className="w-3 h-3 mr-1 flex-shrink-0" />
                        <span className="truncate">{opportunity.email}</span>
                      </div>

                      {opportunity.phone && (
                        <div className="flex items-center text-xs text-gray-600 mb-2">
                          <Phone className="w-3 h-3 mr-1 flex-shrink-0" />
                          <span>{opportunity.phone}</span>
                        </div>
                      )}

                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                        <div className="flex items-center text-sm font-semibold text-gray-900">
                          <DollarSign className="w-4 h-4 mr-1" />
                          {formatCurrency(Number(opportunity.value))}
                        </div>
                        {opportunity.proposal_link && (
                          <ExternalLink className="w-4 h-4 text-blue-600" />
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>

      {selectedOpportunity && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end md:items-center justify-center z-50 p-0 md:p-4">
          <div className="bg-white rounded-t-2xl md:rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] md:max-h-[85vh] overflow-hidden flex flex-col animate-slide-up md:animate-none">
            <div className="p-4 md:p-6 border-b border-gray-200 flex items-center justify-between">
              <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-4 md:hidden"></div>
              <h3 className="text-lg md:text-xl font-bold text-gray-900">Opportunity Details</h3>
              <button
                onClick={() => {
                  setSelectedOpportunity(null);
                  setActiveTab('details');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="border-b border-gray-200">
              <div className="flex">
                <button
                  onClick={() => setActiveTab('details')}
                  className={`flex-1 px-4 md:px-6 py-3 text-sm font-medium transition-colors ${
                    activeTab === 'details'
                      ? 'text-blue-600 border-b-2 border-blue-600'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Details
                </button>
                <button
                  onClick={() => setActiveTab('tasks')}
                  className={`flex-1 px-4 md:px-6 py-3 text-sm font-medium transition-colors flex items-center justify-center space-x-2 ${
                    activeTab === 'tasks'
                      ? 'text-blue-600 border-b-2 border-blue-600'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <ListTodo className="w-4 h-4" />
                  <span className="hidden sm:inline">Tasks</span>
                </button>
                <button
                  onClick={() => setActiveTab('timeline')}
                  className={`flex-1 px-4 md:px-6 py-3 text-sm font-medium transition-colors flex items-center justify-center space-x-2 ${
                    activeTab === 'timeline'
                      ? 'text-blue-600 border-b-2 border-blue-600'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Activity className="w-4 h-4" />
                  <span className="hidden sm:inline">Activity</span>
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 md:p-6">
              {activeTab === 'details' && (
                <div className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                {selectedOpportunity.brand && (
                  <div className="col-span-2 pb-4 border-b border-gray-200">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Brand
                    </label>
                    <p className="text-lg font-semibold text-blue-600">{selectedOpportunity.brand.name}</p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Client Name
                  </label>
                  <p className="text-gray-900">{selectedOpportunity.client_name}</p>
                </div>

                {selectedOpportunity.company_name && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Company Name
                    </label>
                    <p className="text-gray-900">{selectedOpportunity.company_name}</p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <p className="text-gray-900">{selectedOpportunity.email}</p>
                </div>

                {selectedOpportunity.phone && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <p className="text-gray-900">{selectedOpportunity.phone}</p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Value</label>
                  <p className="text-gray-900 font-semibold">
                    {formatCurrency(Number(selectedOpportunity.value))}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Probability
                  </label>
                  <p className="text-gray-900">{selectedOpportunity.probability}%</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Stage</label>
                  <div className="flex items-center space-x-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: selectedOpportunity.pipeline_stage?.color }}
                    ></div>
                    <p className="text-gray-900">{selectedOpportunity.pipeline_stage?.name}</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
                  <p className="text-gray-900 capitalize">{selectedOpportunity.source}</p>
                </div>
              </div>

              {selectedOpportunity.notes && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <p className="text-gray-900 whitespace-pre-wrap">{selectedOpportunity.notes}</p>
                </div>
              )}

              {selectedOpportunity.proposal_link && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Proposal Link
                  </label>
                  <a
                    href={selectedOpportunity.proposal_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 text-blue-600 hover:text-blue-700"
                  >
                    <ExternalLink className="w-4 h-4" />
                    <span>View Proposal</span>
                  </a>
                </div>
              )}
                </div>
              )}

              {activeTab === 'tasks' && (
                <TaskList
                  opportunityId={selectedOpportunity.id}
                  onRefresh={() => loadPipelineData()}
                />
              )}

              {activeTab === 'timeline' && (
                <div>
                  <div className="mb-4 flex items-center justify-between">
                    <h4 className="text-lg font-semibold text-gray-900">Activity History</h4>
                    <button
                      onClick={() => setShowActivityForm(true)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Log Activity</span>
                    </button>
                  </div>
                  <ActivityTimeline
                    opportunityId={selectedOpportunity.id}
                    onRefresh={() => loadPipelineData()}
                  />
                </div>
              )}
            </div>

            <div className="p-4 md:p-6 border-t border-gray-200 flex items-center justify-between sticky bottom-0 bg-white">
              <button
                onClick={() => handleDelete(selectedOpportunity.id)}
                className="px-3 py-2 md:px-4 md:py-2 text-red-600 hover:bg-red-50 active:bg-red-100 rounded-lg flex items-center space-x-2 text-sm md:text-base"
              >
                <Trash2 className="w-4 h-4" />
                <span>Delete</span>
              </button>
              <button
                onClick={() => {
                  onEditOpportunity(selectedOpportunity);
                  setSelectedOpportunity(null);
                  setActiveTab('details');
                }}
                className="px-3 py-2 md:px-4 md:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 flex items-center space-x-2 text-sm md:text-base shadow-sm"
              >
                <Edit className="w-4 h-4" />
                <span>Edit</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {showActivityForm && selectedOpportunity && selectedBrand && (
        <ActivityForm
          opportunityId={selectedOpportunity.id}
          brandId={selectedBrand.id}
          onClose={() => setShowActivityForm(false)}
          onSuccess={() => {
            setShowActivityForm(false);
            loadPipelineData();
          }}
        />
      )}

      {showTaskTemplateManager && (
        <TaskTemplateManager
          onClose={() => setShowTaskTemplateManager(false)}
        />
      )}

      {showAnalytics && (
        <PipelineAnalytics
          onClose={() => setShowAnalytics(false)}
          brandIds={selectedBrandIds}
        />
      )}

      <BulkActions
        selectedOpportunityIds={selectedIds}
        onClearSelection={() => setSelectedIds([])}
        onActionComplete={handleBulkActionComplete}
      />
    </div>
  );
}
